import { forwardRef, useState } from "react";
import aboutImage from "../../Images/about.jpeg.png";
import styles from "./About.module.css";

const App = forwardRef((props, ref) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phoneNumber: "",
    purpose: "",
    message: "",
    captchaAnswer: "",
  });
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("");

  // Generate a simple math CAPTCHA (e.g., "3 + 5 = ?")
  function generateCaptcha() {
    const num1 = Math.floor(Math.random() * 10) + 1;
    const num2 = Math.floor(Math.random() * 10) + 1;
    const answer = num1 + num2;
    return { question: `${num1} + ${num2} = ?`, answer };
  }

  // Handle input changes for form fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Validate form including CAPTCHA
  const validateForm = () => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Name is required";
    if (!formData.email.trim() || !/^\S+@\S+\.\S+$/.test(formData.email))
      newErrors.email = "Valid email is required";
    if (!formData.phoneNumber.trim())
      newErrors.phoneNumber = "Phone number is required";
    if (!formData.purpose) newErrors.purpose = "Please select a purpose";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    if (!formData.captchaAnswer.trim() || parseInt(formData.captchaAnswer) !== captcha.answer)
      newErrors.captcha = "Incorrect CAPTCHA answer";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setStatus("");
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phoneNumber: formData.phoneNumber,
        purpose: formData.purpose,
        message: formData.message,
      };

      const response = await fetch(
        "https://prod.toxboxai.com/api/contactdata/createContactData",
        "https://prod.toxboxai.com/api/contactdata/createContactData",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.error || `HTTP error! Status: ${response.status}`
        );
      }

      setStatus("Form submitted successfully!");
      setFormData({
        name: "",
        email: "",
        phoneNumber: "",
        purpose: "",
        message: "",
        captchaAnswer: "",
      });
      setCaptcha(generateCaptcha()); // Generate new CAPTCHA after submission
    } catch (error) {
      setStatus(`Failed to submit form: ${error.message}`);
    }
  };

  return (
    <div className={styles.mainContainer} ref={ref}>
      <div className={styles.aboutSection}>
        <div className={styles.imageContainer}>
          <img
            src={aboutImage}
            alt="Person with headset"
            className={styles.aboutImage}
          />
        </div>
        <div className={styles.aboutTextContainer}>
          <div className={styles.aboutHeader}>
            <span className={styles.bit}>A BIT</span>
            <span className={styles.aboutUs}> ABOUT US</span>
          </div>
          <p className={styles.aboutTitle}>
            ToxBox is a modern AI-powered communication platform built for
            businesses of all sizes—from startups to enterprises. Our mission is
            to simplify customer interactions by combining advanced AI with
            intuitive design.
            <br />
            <br />
            With real-time call analytics, automated responses, and seamless
            integrations, ToxBox helps you reduce response times, increase
            productivity, and create meaningful connections that drive growth.
          </p>
        </div>
      </div>

      <div id="contact" className={styles.contactSection}>
        <div className={styles.contactTextContainer}>
          <h1 className={styles.contactTitle}>Contact Us</h1>
          <p className={styles.contactSubtitle}>
            <span className={styles.subtitleLine}>Let's Make</span>
            <span className={styles.communication}> Communication</span>
            <span className={styles.Easer}> Easier—</span>
            <span className={styles.subtitleLine}> Together</span>
          </p>
          <p className={styles.contactDescription}>
            We’re happy to answer your
          </p>
          <p className={styles.contactDescription}>
            questions or walk you through
          </p>
          <p className={styles.contactDescription}>
            our platform. Let us know{" "}
          </p>
          <p className={styles.contactDescription}>how we can help</p>
          <div className={styles.contactDetails}>
            <p className={styles.contactEmail}>
              <span className={styles.emailIcon}>📧</span> sales@provaantech.com
            </p>
            <p className={styles.contactPhone}>
              <span className={styles.phoneIcon}>📞</span> 8431498169
            </p>
          </div>
        </div>
        <div className={styles.contactFormContainer}>
          <form onSubmit={handleSubmit}>
            <div className={styles.formFieldContainer}>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Name</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={styles.inputName}
                />
                {errors.name && (
                  <span className="text-red-500 text-sm">{errors.name}</span>
                )}
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={styles.inputEmail}
                />
                {errors.email && (
                  <span className="text-red-500 text-sm">{errors.email}</span>
                )}
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Phone no.</label>
                <input
                  type="tel"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleInputChange}
                  className={styles.inputPhone}
                />
                {errors.phoneNumber && (
                  <span className="text-red-500 text-sm">
                    {errors.phoneNumber}
                  </span>
                )}
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>Purpose</label>
                <select
                  name="purpose"
                  value={formData.purpose}
                  onChange={handleInputChange}
                  className={styles.inputName}
                >
                  <option value="">Select a purpose</option>
                  <option value="General Contact">General Contact</option>
                  <option value="Inquiry">Inquiry</option>
                  <option value="Demo">Demo</option>
                  <option value="Developer Meeting">Developer Meeting</option>
                </select>
                {errors.purpose && (
                  <span className="text-red-500 text-sm">{errors.purpose}</span>
                )}
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  Solve this: {captcha.question}
                </label>
                <input
                  type="number"
                  name="captchaAnswer"
                  value={formData.captchaAnswer}
                  onChange={handleInputChange}
                  className={styles.inputName}
                  placeholder="Enter answer"
                />
                {errors.captcha && (
                  <span className="text-red-500 text-sm">{errors.captcha}</span>
                )}
              </div>
              <div className={styles.formField}>
                <label className={styles.formLabel}>
                  How can Tox Box help you grow?
                </label>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleInputChange}
                  className={styles.textareaMessage}
                ></textarea>
                {errors.message && (
                  <span className="text-red-500 text-sm">{errors.message}</span>
                )}
              </div>

            </div>
            <button type="submit" className={styles.submitButton}>
              Submit
            </button>
            {status && (
              <p
                className={`text-center mt-4 ${status.includes("Failed") ? "text-red-500" : "text-green-500"
                  }`}
              >
                {status}
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
});

export default App;